import React, { useState } from 'react';
import { BellIcon, CheckCircleIcon, PlaneIcon, AlertTriangleIcon, ClockIcon, ChevronRightIcon, CheckIcon, MegaphoneIcon, SettingsIcon, MailIcon, SmartphoneIcon, MonitorIcon, SendIcon, XIcon, TagIcon, CalendarIcon, GlobeIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
export function NotificationsPage() {
  const {
    notifications,
    markAllNotificationsRead,
    markNotificationRead
  } = useAppContext();
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();
  const getActionRoute = (action: string | null): string | null => {
    if (!action) return null;
    switch (action) {
      case 'View Request':
      case 'View Trip':
        return '/approvals';
      case 'View Booking':
      case 'View Details':
        return '/trips';
      case 'Review Policy':
        return '/refund-policies';
      case 'View Report':
        return '/reports';
      case 'View Event':
      case 'View Offer':
        return '/events';
      default:
        return null;
    }
  };
  const handleNotificationAction = (notificationId: number, action: string | null) => {
    markNotificationRead(notificationId);
    const route = getActionRoute(action);
    if (route) {
      navigate(route);
    }
  };
  const tabs = [{
    id: 'all',
    label: 'All',
    count: notifications.length
  }, {
    id: 'operational',
    label: 'Operational',
    count: notifications.filter(n => n.category === 'operational').length
  }, {
    id: 'marketing',
    label: 'Marketing',
    count: notifications.filter(n => n.category === 'marketing').length
  }, {
    id: 'settings',
    label: 'Settings',
    count: 0
  }];
  const getIcon = (type: string) => {
    switch (type) {
      case 'approval':
        return CheckCircleIcon;
      case 'booking':
        return PlaneIcon;
      case 'policy':
        return AlertTriangleIcon;
      case 'marketing':
        return MegaphoneIcon;
      case 'operational':
        return ClockIcon;
      case 'system':
        return BellIcon;
      default:
        return BellIcon;
    }
  };
  const getColor = (type: string) => {
    switch (type) {
      case 'approval':
        return 'bg-emerald-100 text-emerald-600';
      case 'booking':
        return 'bg-blue-100 text-blue-600';
      case 'policy':
        return 'bg-amber-100 text-amber-600';
      case 'marketing':
        return 'bg-purple-100 text-purple-600';
      case 'operational':
        return 'bg-navy-100 text-navy-600';
      case 'system':
        return 'bg-slate-100 text-slate-600';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };
  const filteredNotifications = activeTab === 'all' ? notifications : activeTab === 'settings' ? [] : notifications.filter(n => n.category === activeTab);
  const [channelPrefs, setChannelPrefs] = useState({
    bookingConfirmations: {
      email: true,
      sms: true,
      inApp: true,
      push: true
    },
    approvalUpdates: {
      email: true,
      sms: false,
      inApp: true,
      push: true
    },
    ticketIssuance: {
      email: true,
      sms: true,
      inApp: true,
      push: false
    },
    delays: {
      email: true,
      sms: true,
      inApp: true,
      push: true
    },
    cancellations: {
      email: true,
      sms: true,
      inApp: true,
      push: true
    },
    refundUpdates: {
      email: true,
      sms: false,
      inApp: true,
      push: false
    },
    eventPromotions: {
      email: true,
      sms: false,
      inApp: true,
      push: true
    },
    partnerOffers: {
      email: false,
      sms: false,
      inApp: true,
      push: false
    }
  });
  return <div className="space-y-6" data-id="element-1698">
      <div className="flex items-center justify-between" data-id="element-1699">
        <div data-id="element-1700">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-1701">Notifications</h2>
          <p className="text-slate-500" data-id="element-1702">
            Stay updated on travel activity and marketing opportunities.
          </p>
        </div>
        <button onClick={markAllNotificationsRead} className="flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1703">
          <CheckIcon className="mr-2 h-4 w-4" data-id="element-1704" />
          Mark All Read
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1" data-id="element-1705">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1706">
            {tab.label}
            {tab.count > 0 && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-navy-100 text-navy-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-1707">
                {tab.count}
              </span>}
          </button>)}
      </div>

      {activeTab === 'settings' /* Notification Settings */ ? <div className="space-y-6" data-id="element-1708">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-id="element-1709">
            <div className="border-b border-slate-200 px-6 py-4" data-id="element-1710">
              <h3 className="font-semibold text-navy-900" data-id="element-1711">
                Channel Preferences
              </h3>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1712">
                Choose how you want to receive each type of notification.
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-1713">
              <table className="w-full text-left text-sm" data-id="element-1714">
                <thead className="bg-slate-50 text-slate-500" data-id="element-1715">
                  <tr data-id="element-1716">
                    <th className="px-6 py-3 font-medium" data-id="element-1717">Notification Type</th>
                    <th className="px-6 py-3 font-medium text-center" data-id="element-1718">
                      <MailIcon className="h-4 w-4 mx-auto" data-id="element-1719" />
                    </th>
                    <th className="px-6 py-3 font-medium text-center" data-id="element-1720">
                      <SmartphoneIcon className="h-4 w-4 mx-auto" data-id="element-1721" />
                    </th>
                    <th className="px-6 py-3 font-medium text-center" data-id="element-1722">
                      <MonitorIcon className="h-4 w-4 mx-auto" data-id="element-1723" />
                    </th>
                    <th className="px-6 py-3 font-medium text-center" data-id="element-1724">
                      <SendIcon className="h-4 w-4 mx-auto" data-id="element-1725" />
                    </th>
                  </tr>
                  <tr data-id="element-1726">
                    <th className="px-6 pb-2" data-id="element-1727"></th>
                    <th className="px-6 pb-2 text-center text-[10px] font-normal" data-id="element-1728">
                      Email
                    </th>
                    <th className="px-6 pb-2 text-center text-[10px] font-normal" data-id="element-1729">
                      SMS
                    </th>
                    <th className="px-6 pb-2 text-center text-[10px] font-normal" data-id="element-1730">
                      In-App
                    </th>
                    <th className="px-6 pb-2 text-center text-[10px] font-normal" data-id="element-1731">
                      Push
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-1732">
                  {[{
                key: 'bookingConfirmations',
                label: 'Booking Confirmations',
                category: 'Operational'
              }, {
                key: 'approvalUpdates',
                label: 'Approval Updates',
                category: 'Operational'
              }, {
                key: 'ticketIssuance',
                label: 'Ticket Issuance',
                category: 'Operational'
              }, {
                key: 'delays',
                label: 'Delays & Disruptions',
                category: 'Operational'
              }, {
                key: 'cancellations',
                label: 'Cancellations',
                category: 'Operational'
              }, {
                key: 'refundUpdates',
                label: 'Refund Updates',
                category: 'Operational'
              }, {
                key: 'eventPromotions',
                label: 'Event Promotions',
                category: 'Marketing'
              }, {
                key: 'partnerOffers',
                label: 'Partner Offers',
                category: 'Marketing'
              }].map(item => <tr key={item.key} className="hover:bg-slate-50" data-id="element-1733">
                      <td className="px-6 py-3" data-id="element-1734">
                        <div data-id="element-1735">
                          <span className="font-medium text-slate-900" data-id="element-1736">
                            {item.label}
                          </span>
                          <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${item.category === 'Marketing' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-1737">
                            {item.category}
                          </span>
                        </div>
                      </td>
                      {['email', 'sms', 'inApp', 'push'].map(channel => <td key={channel} className="px-6 py-3 text-center" data-id="element-1738">
                          <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-navy-700 focus:ring-navy-600" defaultChecked={(channelPrefs as any)[item.key]?.[channel]} data-id="element-1739" />
                        </td>)}
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Campaign Section (Admin) */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-1740">
            <h3 className="font-semibold text-navy-900 mb-4" data-id="element-1741">
              Marketing Campaigns (Admin)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1742">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1743">
                <h4 className="font-medium text-slate-900 mb-2" data-id="element-1744">
                  Create Campaign
                </h4>
                <div className="space-y-3" data-id="element-1745">
                  <input type="text" className="w-full rounded-lg border border-slate-300 p-2 text-sm" placeholder="Campaign name" data-id="element-1746" />
                  <select className="w-full rounded-lg border border-slate-300 p-2 text-sm" data-id="element-1747">
                    <option data-id="element-1748">All Industries</option>
                    <option data-id="element-1749">Pharmaceutical</option>
                    <option data-id="element-1750">Technology</option>
                    <option data-id="element-1751">Finance</option>
                  </select>
                  <input type="datetime-local" className="w-full rounded-lg border border-slate-300 p-2 text-sm" data-id="element-1752" />
                  <textarea rows={2} className="w-full rounded-lg border border-slate-300 p-2 text-sm" placeholder="Campaign message..." data-id="element-1753" />
                  <button className="w-full rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-white hover:bg-navy-700" data-id="element-1754">
                    Schedule Campaign
                  </button>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1755">
                <h4 className="font-medium text-slate-900 mb-2" data-id="element-1756">
                  In-App Banner Preview
                </h4>
                <div className="rounded-lg bg-gradient-to-r from-navy-900 to-navy-700 p-4 text-white" data-id="element-1757">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1758">
                    <MegaphoneIcon className="h-4 w-4 text-gold-400" data-id="element-1759" />
                    <span className="text-xs font-bold text-gold-400 uppercase" data-id="element-1760">
                      Promotion
                    </span>
                  </div>
                  <p className="text-sm font-semibold" data-id="element-1761">
                    20% off Emirates Business Class
                  </p>
                  <p className="text-xs text-slate-300 mt-1" data-id="element-1762">
                    Book by Dec 31 for exclusive corporate rates
                  </p>
                  <button className="mt-3 rounded-lg bg-gold-500 px-3 py-1 text-xs font-bold text-navy-900" data-id="element-1763">
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div> /* Notification List */ : <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100" data-id="element-1764">
          {filteredNotifications.length > 0 ? filteredNotifications.map(notification => {
        const Icon = getIcon(notification.type);
        const colorClass = getColor(notification.type);
        return <div key={notification.id} className={`p-4 flex items-start gap-4 hover:bg-slate-50 transition-colors ${!notification.read ? 'bg-navy-50/20' : ''}`} data-id="element-1765">
                  <div className={`mt-1 h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`} data-id="element-1766">
                    <Icon className="h-5 w-5" data-id="element-1767" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1768">
                    <div className="flex items-center justify-between mb-1" data-id="element-1769">
                      <div className="flex items-center gap-2" data-id="element-1770">
                        <h3 className={`text-sm font-semibold ${!notification.read ? 'text-navy-900' : 'text-slate-700'}`} data-id="element-1771">
                          {notification.title}
                        </h3>
                        {notification.category && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${notification.category === 'marketing' ? 'bg-purple-100 text-purple-700' : notification.category === 'operational' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-1772">
                            {notification.category}
                          </span>}
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap ml-2" data-id="element-1773">
                        {notification.time}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-2" data-id="element-1774">
                      {notification.desc}
                    </p>
                    {notification.action && <button onClick={() => handleNotificationAction(notification.id, notification.action)} className="text-sm font-medium text-navy-700 hover:text-navy-900 flex items-center" data-id="element-1775">
                        {notification.action}{' '}
                        <ChevronRightIcon className="h-3 w-3 ml-1" data-id="element-1776" />
                      </button>}
                  </div>
                  {!notification.read && <div className="mt-2 h-2.5 w-2.5 rounded-full bg-navy-700 flex-shrink-0" data-id="element-1777" />}
                </div>;
      }) : <div className="p-12 text-center text-slate-500" data-id="element-1778">
              <BellIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" data-id="element-1779" />
              <p data-id="element-1780">No notifications found</p>
            </div>}
        </div>}
    </div>;
}